import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { unitsToMicros } from 'src/app/models/Wallet';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { AgentDraft, AgentTestMessage, AgentTestReply, AiAgentService } from 'src/app/shared/services/ai-agent.service';

/**
 * Aviso fixo do painel (CU-17tm466qj1v): o teste chama o modelo de verdade e é cobrado como uma
 * conversa (AiAgentController::runTest → BillingService::chargeUsage), para toda conta — a IA sai
 * da carteira por flag global, não por coorte. Sem valor estimado aqui: o custo por mensagem com
 * markup ainda está em verificação (17tm466qj1q); o valor real aparece em cada resposta.
 *
 * Assume a IA cobrada pela carteira (`WALLET_AI_ENABLED`, ligada em PROD desde 18/10). Se a flag
 * for desligada, a cobrança volta ao pacote de tokens e este texto precisa acompanhar.
 */
const TEST_BILLING_NOTICE = 'As mensagens de teste também são cobradas da sua carteira, pelo mesmo valor de uma conversa real.';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Quanto a resposta custou no saldo da carteira, em micros de R$. Sem tokens nem provedor. */
  costMicros?: number | null;
}

/**
 * "Testar agente": conversa de teste com o agente salvo (`agentId`) ou com o rascunho do formulário
 * curto do passo de WhatsApp (`draft`, CU-17tm466mg5z).
 *
 * Com o agente salvo, os testes ficam 7 dias no backend por agente + usuário e reaparecem ao abrir;
 * o "Limpar conversa" apaga o histórico guardado. O rascunho vive só no formulário — ao criar o
 * agente, o backend passa o teste para ele pelo `draft_key`.
 */
@Component({
  selector: 'app-agent-test-panel',
  templateUrl: './agent-test-panel.component.html',
  styleUrls: ['./agent-test-panel.component.scss'],
})
export class AgentTestPanelComponent implements OnChanges, OnDestroy, AfterViewChecked {

  @Input() agentId: string;
  /** Rascunho do formulário curto: testa antes de salvar. Ignorado quando há `agentId`. */
  @Input() draft: AgentDraft | null = null;
  /** Rascunho incompleto: o que falta para testar (ex.: "Escolha o modelo e escreva o prompt"). */
  @Input() draftHint = 'Preencha o modelo e o prompt para testar.';
  @Input() compact = false;

  @ViewChild('messagesContainer') private messagesContainer: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput') private messageInput: ElementRef<HTMLTextAreaElement>;

  readonly billingNotice = TEST_BILLING_NOTICE;

  testMessage = '';
  isTesting = false;
  loadingHistory = false;
  messages: ChatMessage[] = [];

  private subscriptions: Subscription[] = [];
  private historySub: Subscription | null = null;
  private shouldScroll = false;

  constructor(
    private toastService: ToastService,
    private aiAgentService: AiAgentService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.agentId && this.agentId && changes.agentId.currentValue !== changes.agentId.previousValue) {
      this.loadHistory();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.historySub?.unsubscribe();
  }

  /** Dá para conversar: agente salvo, ou rascunho com modelo e prompt. */
  get ready(): boolean {
    return !!this.agentId || this.draftReady;
  }

  get draftReady(): boolean {
    return !!this.draft && !!(this.draft.base_prompt || '').trim() && !!this.draft.smsfunnel_ai_model_id;
  }

  get canTest(): boolean {
    return this.ready && !!this.testMessage.trim() && !this.isTesting;
  }

  loadHistory(): void {
    this.historySub?.unsubscribe();
    this.loadingHistory = true;
    this.historySub = this.aiAgentService.testHistory(this.agentId).subscribe({
      next: res => {
        this.loadingHistory = false;
        // Uma conversa já em andamento na tela vale mais do que a guardada.
        if (!this.messages.length) {
          this.messages = (res?.data || []).map(m => this.fromHistory(m));
          this.shouldScroll = true;
        }
      },
      // Sem histórico a conversa só começa vazia: não é motivo para aviso.
      error: () => this.loadingHistory = false,
    });
  }

  sendMessage(): void {
    if (!this.canTest) { return; }

    const userText = this.testMessage.trim();
    const history = this.messages.map(m => ({ role: m.role, content: m.content }));

    this.messages.push({ role: 'user', content: userText });
    this.testMessage = '';
    this.isTesting = true;
    this.shouldScroll = true;

    const request$ = this.agentId
      ? this.aiAgentService.test(this.agentId, userText, history)
      : this.aiAgentService.testDraft(this.draft as AgentDraft, userText, history);

    this.subscriptions.push(
      request$.subscribe({
        next: (result: AgentTestReply) => {
          this.isTesting = false;
          this.messages.push({ role: 'assistant', content: result?.content || '', costMicros: this.toMicros(result?.cost_brl) });
          this.shouldScroll = true;
        },
        error: (res) => {
          this.isTesting = false;
          this.toastService.error(res?.error?.message || 'Erro ao testar agente!');
        }
      })
    );

    if (this.messageInput) {
      this.messageInput.nativeElement.style.height = 'auto';
    }
  }

  autoGrow(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  /** Limpa a tela e, com o agente salvo, o histórico guardado (senão ele voltaria ao reabrir). */
  clearChat(): void {
    this.messages = [];
    if (this.agentId) {
      this.subscriptions.push(
        this.aiAgentService.clearTestHistory(this.agentId).subscribe({
          error: () => this.toastService.error('Não foi possível apagar o histórico do teste.'),
        })
      );
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private fromHistory(message: AgentTestMessage): ChatMessage {
    return { role: message.role, content: message.content, costMicros: this.toMicros(message.cost_brl) };
  }

  /** O backend manda R$ como string decimal ("0.012345"); o pipe de dinheiro trabalha em micros. */
  private toMicros(value: string | null | undefined): number | null {
    if (value === null || value === undefined || value === '' || !isFinite(Number(value))) {
      return null;
    }
    return unitsToMicros(Number(value));
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }
}
